import { EmptyPage, TableV2 } from '@medeloop/medeloop-components-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCodesets, useGetCodesetsByDataSourceId } from 'src/api/codesets-queries';
import { DocumentIcon } from 'src/assets/svg';

export const DatasetCodes = () => {
  const [tableSearchInput, setTableSearchInput] = useState<string>('');
  const { t } = useTranslation();
  const { datasetId } = useParams();
  const navigate = useNavigate();

  const { isLoading, data } = datasetId
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetCodesetsByDataSourceId(datasetId ?? '')
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetCodesets();

  const headers = [
    { label: 'Code', key: 'name' },
    { label: 'Creator', key: 'creator' },
    {
      label: '# Codes',
      key: 'num_of_codes',
    },
    { label: 'Updated', key: 'updated' },
  ];

  const conceptIds = useMemo(
    () =>
      Array.from(
        new Set(
          data?.data.codesets
            .map((codeset) => codeset.data.map((code) => code.concept_id))
            .flat()
            .filter((conceptId) => conceptId !== undefined),
        ),
      ),
    [data],
  );

  const transformedData = useMemo(
    () =>
      (data?.data?.codesets || [])
        .filter(
          (codeset) =>
            codeset.codesetName.toLowerCase().includes(tableSearchInput.toLowerCase()) ||
            codeset.author?.toLowerCase().includes(tableSearchInput.toLowerCase()) ||
            codeset.codesetDescription?.toLowerCase().includes(tableSearchInput.toLowerCase()),
        )
        .map((codeset) => ({
          id: codeset.id,
          name: (
            <>
              <div className="flex flex-col">
                <span className="text-base font-semibold">{codeset.codesetName}</span>
                <span className="text-sm text-on-dark-subdued-content">
                  {codeset.codesetDescription}
                </span>
              </div>
            </>
          ),
          creator: codeset.author,
          num_of_codes: codeset.data.length,
          updated: codeset.createdAt
            ? new Date(codeset.createdAt).toISOString().split('T')[0] // Format as "YYYY-MM-DD"
            : '',
          onClick: () => navigate(`/account/data/codes/${codeset.id}`),
        })),
    [data, conceptIds, tableSearchInput, navigate],
  );

  if (!isLoading && !data?.data.codesets?.length) {
    return (
      <div className="flex flex-col items-center gap-4 p-4 rounded-lg h-[calc(100vh-350px)]">
        <EmptyPage
          className="flex flex-col items-center justify-center h-full w-full"
          pageIcon={<DocumentIcon className="w-[37px] h-[37px]" />}
          header={t('no_codes_created')}
          description={t('no_codes_created_description')}
        />
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <TableV2
        headers={headers}
        data={transformedData}
        isLoading={isLoading}
        searchPlaceholder="Search codes"
        onSearch={(t) => setTableSearchInput(t)}
        withDivider={false}
      />
    </section>
  );
};
